import React, { useEffect, useState } from 'react';
import { Box, Typography, Table, Chip, Button, IconButton, Tooltip, CircularProgress, Switch } from '@mui/joy';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';

import { adminApi } from './adminApi';

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const AdminDashboard = () => {
  const [posts, setPosts] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const fetchPosts = async () => {
    try {
      const { data } = await adminApi.get('/blogs');
      setPosts(data);
    } catch (err) {
      toast.error('Failed to load posts');
      setPosts([]);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  const togglePublished = async (post) => {
    try {
      await adminApi.put(`/blogs/${post.id}`, { published: !post.published });
      toast.success(post.published ? 'Unpublished' : 'Published');
      fetchPosts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const handleDelete = async (post) => {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    setDeletingId(post.id);
    try {
      await adminApi.delete(`/blogs/${post.id}`);
      toast.success('Post deleted');
      fetchPosts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography
            level="h2"
            sx={{
              fontWeight: 700,
              mb: 1,
              background: 'linear-gradient(135deg, #1B365D 0%, #2E5BBA 50%, #00A86B 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Blog Posts
          </Typography>
          <Typography level="body2" sx={{ color: 'text.secondary' }}>
            {posts ? `${posts.length} total` : 'Loading...'}
          </Typography>
        </Box>
        <Button
          component={Link}
          to="/admin/blog/new"
          size="lg"
          startDecorator={<AddIcon />}
          sx={{
            background: 'linear-gradient(135deg, #2E5BBA 0%, #00A86B 100%)',
            color: 'white',
            fontWeight: 600,
            '&:hover': {
              background: 'linear-gradient(135deg, #1B365D 0%, #2E5BBA 100%)',
            },
          }}
        >
          New Post
        </Button>
      </Box>

      {!posts && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {posts && posts.length === 0 && (
        <Box
          sx={{
            p: 6,
            textAlign: 'center',
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 2,
          }}
        >
          <Typography level="h4" sx={{ color: 'text.primary', mb: 2 }}>
            No blog posts yet
          </Typography>
          <Typography level="body1" sx={{ color: 'text.secondary', mb: 3 }}>
            Create your first post or run the migration script to import existing posts.
          </Typography>
          <Button
            component={Link}
            to="/admin/blog/new"
            startDecorator={<AddIcon />}
            sx={{
              background: 'linear-gradient(135deg, #2E5BBA 0%, #00A86B 100%)',
              color: 'white',
            }}
          >
            Create First Post
          </Button>
        </Box>
      )}

      {posts && posts.length > 0 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}>
          <Box
            sx={{
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <Table hoverRow sx={{ '--TableCell-paddingY': '12px' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Title</th>
                  <th style={{ textAlign: 'left', width: 120 }}>Date</th>
                  <th style={{ textAlign: 'left', width: 140 }}>Status</th>
                  <th style={{ textAlign: 'right', width: 180 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {posts.map((post) => (
                  <tr key={post.id}>
                    <td>
                      <Box>
                        <Typography level="body2" sx={{ fontWeight: 600, color: '#ffffff', mb: 0.5 }}>
                          {post.title}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {post.tags && post.tags.slice(0, 3).map((tag) => (
                            <Chip
                              key={tag}
                              size="sm"
                              variant="soft"
                              sx={{
                                background: 'rgba(46, 91, 186, 0.1)',
                                color: '#2E5BBA',
                                fontSize: '0.7rem',
                              }}
                            >
                              {tag}
                            </Chip>
                          ))}
                        </Box>
                      </Box>
                    </td>
                    <td>
                      <Typography level="body2" sx={{ color: 'text.secondary' }}>
                        {formatDate(post.date)}
                      </Typography>
                    </td>
                    <td>
                      <Switch
                        checked={post.published}
                        onChange={() => togglePublished(post)}
                        color={post.published ? 'success' : 'neutral'}
                        endDecorator={
                          <Typography level="body2" sx={{ color: 'text.secondary' }}>
                            {post.published ? 'Published' : 'Draft'}
                          </Typography>
                        }
                      />
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <Tooltip title="View on site" placement="top">
                        <IconButton
                          component={Link}
                          to={`/blog/${post.slug}`}
                          target="_blank"
                          size="sm"
                          variant="plain"
                          color="neutral"
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit" placement="top">
                        <IconButton
                          component={Link}
                          to={`/admin/blog/edit/${post.id}`}
                          size="sm"
                          variant="plain"
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete" placement="top">
                        <IconButton
                          size="sm"
                          variant="plain"
                          color="danger"
                          loading={deletingId === post.id}
                          onClick={() => handleDelete(post)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Box>
        </motion.div>
      )}
    </Box>
  );
};

export default AdminDashboard;
